import React, { useContext, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Snackbar,
  Alert,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import AuthContext from '../../context/AuthContext';
import AccountsList, { VERIFICATION_META } from './AccountsTable/AccountsList';
import BulkActions from './AccountsTable/BulkActions';
import useAccountsData from './hooks/useAccountsData';
import useBulkSelection from './hooks/useBulkSelection';
import { whatsappAccountService } from '../../services/api';
import { INK, MUTED, formatCount } from '../analytics/analyticsUi';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' }
];

const WhatsAppAccounts = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  const [success, setSuccess] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(null); // { account, businessName, businessDescription, selfPay }
  const [saving, setSaving] = useState(false);

  const {
    accounts,
    loading,
    error,
    totalAccounts,
    refreshingAccounts,
    page,
    rowsPerPage,
    searchInput,
    filters,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearchChange,
    handleFilterChange,
    clearFilters,
    fetchAccounts,
    refreshAccount,
    bulkRefresh,
    removeAccount,
    updateAccount,
    setError
  } = useAccountsData();

  const {
    selectedCount,
    isSelected,
    isAllSelected,
    isIndeterminate,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    getSelectedItems
  } = useBulkSelection(accounts || []);

  const hasFilters = !!(searchInput || filters.status || filters.verification);

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setSuccess(`${label} copied`);
  };

  const handleEdit = (account) => {
    setEditing({
      account,
      businessName: account.businessName || '',
      businessDescription: account.businessDescription || '',
      selfPay: !!account.selfPay
    });
  };

  const saveEdit = async () => {
    const { account, businessName, businessDescription, selfPay } = editing;
    try {
      setSaving(true);
      const changes = { businessName: businessName.trim(), businessDescription };
      if (isAdmin) changes.selfPay = selfPay;
      const response = await whatsappAccountService.updateAccount(account._id, changes);
      // Merge only what was edited; the response has the owner as a bare id
      const saved = response.data || {};
      updateAccount(account._id, {
        businessName: saved.businessName,
        businessDescription: saved.businessDescription,
        ...(isAdmin ? { selfPay: saved.selfPay } : {})
      });
      setSuccess(`${saved.businessName || account.businessName} updated`);
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.error || 'The account could not be updated. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeleting(true);
      await whatsappAccountService.deleteAccount(pendingDelete._id);
      removeAccount(pendingDelete._id);
      setSuccess(`${pendingDelete.businessName} deleted`);
      setPendingDelete(null);
    } catch (err) {
      setError(err.response?.data?.error || 'The account could not be deleted. Try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewToken = async (account) => {
    try {
      const response = await whatsappAccountService.getAccountToken(account._id);
      if (response.token) {
        await navigator.clipboard.writeText(response.token);
        setSuccess(`Access token for ${account.businessName} copied`);
      } else {
        setError('This account has no access token. Reconnect it to get one.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'The access token could not be copied.');
    }
  };

  const handleRefresh = async (account) => {
    const result = await refreshAccount(account);
    if (result?.success) setSuccess(`${account.businessName} refreshed from Meta`);
  };

  const handleBulkRefresh = async (accountIds) => {
    const results = await bulkRefresh(accountIds);
    const successCount = results.filter((r) => r.success).length;
    const failCount = results.length - successCount;
    if (failCount) setError(`${failCount} of ${results.length} accounts could not be refreshed`);
    else setSuccess(`${successCount} accounts refreshed from Meta`);
    clearSelection();
    return results;
  };

  const handleBulkDelete = async (accountIds) => {
    let deleted = 0;
    const failed = [];
    for (const accountId of accountIds) {
      try {
        await whatsappAccountService.deleteAccount(accountId);
        removeAccount(accountId);
        deleted += 1;
      } catch (err) {
        failed.push(accountId);
      }
    }
    if (failed.length) setError(`${deleted} deleted, ${failed.length} could not be deleted`);
    else setSuccess(`${deleted} accounts deleted`);
  };

  const connectNew = () => navigate('/whatsapp-onboarding');

  return (
    <AppLayout>
      <Container maxWidth="lg" sx={{ pb: selectedCount ? 12 : 6 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
          <Box>
            <Typography
              component="h1"
              sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}
            >
              WhatsApp accounts
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>
              {loading
                ? 'Loading accounts…'
                : `${formatCount(totalAccounts)} ${totalAccounts === 1 ? 'account' : 'accounts'}${hasFilters ? ' match your filters' : isAdmin ? ' across all customers' : ''}`}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={connectNew} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Connect WhatsApp Business
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search name, phone number or WABA ID"
            value={searchInput}
            onChange={handleSearchChange}
            inputProps={{ 'aria-label': 'Search accounts' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: MUTED }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => handleSearchChange({ target: { value: '' } })} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{ flex: { xs: '1 1 100%', md: '1 1 320px' }, maxWidth: { md: 420 }, bgcolor: 'background.paper' }}
          />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={filters.status || ''}
            onChange={(e, value) => value !== null && handleFilterChange('status', value)}
            aria-label="Filter by status"
          >
            {STATUS_FILTERS.map((option) => (
              <ToggleButton key={option.label} value={option.value} sx={{ textTransform: 'none', px: 1.5 }}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="verification-filter-label">Verification</InputLabel>
            <Select
              labelId="verification-filter-label"
              label="Verification"
              value={filters.verification || ''}
              onChange={(e) => handleFilterChange('verification', e.target.value)}
            >
              <MenuItem value="">Any verification</MenuItem>
              {Object.entries(VERIFICATION_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>{meta.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasFilters && (
            <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none', color: MUTED }}>
              Clear filters
            </Button>
          )}
        </Box>

        <AccountsList
          accounts={accounts || []}
          loading={loading}
          error={error && !accounts?.length ? error : null}
          hasFilters={hasFilters}
          showOwner={isAdmin}
          page={page}
          rowsPerPage={rowsPerPage}
          totalAccounts={totalAccounts}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          isSelected={isSelected}
          isAllSelected={isAllSelected}
          isIndeterminate={isIndeterminate}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          refreshingAccounts={refreshingAccounts || {}}
          onCopy={copyText}
          onViewAnalytics={(account) => navigate(`/whatsapp-analytics/${account._id}`)}
          onRefresh={handleRefresh}
          onViewToken={handleViewToken}
          onEdit={handleEdit}
          onDelete={setPendingDelete}
          onConnectNew={connectNew}
          onRetry={fetchAccounts}
          onClearFilters={clearFilters}
        />

        <BulkActions
          selectedCount={selectedCount}
          selectedItems={getSelectedItems()}
          onClearSelection={clearSelection}
          onBulkRefresh={handleBulkRefresh}
          onBulkDelete={handleBulkDelete}
        />

        {/* Edit account */}
        <Dialog open={!!editing} onClose={() => !saving && setEditing(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Edit {editing?.account.verifiedName || editing?.account.businessName}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
              {editing?.account.phoneNumber}{editing?.account.wabaId ? `, WABA ID ${editing.account.wabaId}` : ''}
            </Typography>
            {editing && (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  label="Business name"
                  size="small"
                  value={editing.businessName}
                  onChange={(e) => setEditing({ ...editing, businessName: e.target.value })}
                  required
                />
                <TextField
                  label="Description"
                  size="small"
                  multiline
                  minRows={2}
                  value={editing.businessDescription}
                  onChange={(e) => setEditing({ ...editing, businessDescription: e.target.value })}
                />
                {isAdmin && (
                  <Box>
                    <FormControlLabel
                      control={<Switch checked={editing.selfPay} onChange={(e) => setEditing({ ...editing, selfPay: e.target.checked })} />}
                      label={<Typography variant="body2" sx={{ color: INK, fontWeight: 500 }}>Self-pay: the customer pays Meta for messages with their own card</Typography>}
                    />
                    <Typography variant="caption" sx={{ color: MUTED, display: 'block', ml: 6 }}>
                      No message charges invoices are created for this account. Subscription invoices still are,
                      and message usage and cost stay visible in analytics.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setEditing(null)} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              onClick={saveEdit}
              variant="contained"
              disabled={saving || !editing?.businessName.trim()}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Single delete confirmation */}
        <Dialog open={!!pendingDelete} onClose={() => !deleting && setPendingDelete(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Delete {pendingDelete?.businessName}?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: MUTED }}>
              {pendingDelete?.phoneNumber} is removed from the portal. The number stays registered with Meta. This can't be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setPendingDelete(null)} disabled={deleting} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none' }}
            >
              {deleting ? 'Deleting…' : 'Delete account'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!(error && accounts?.length)}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!success}
          autoHideDuration={4000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
      </Container>
    </AppLayout>
  );
};

export default WhatsAppAccounts;
